import { useCallback, useEffect, useState } from 'react';
import {
  canShowIosInstallGuide,
  getPwaPlatform,
  isStandaloneMode,
} from '../lib/pwa';

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => isStandaloneMode());
  const [platform, setPlatform] = useState(() => getPwaPlatform());

  useEffect(() => {
    setPlatform(getPwaPlatform());
    setIsInstalled(isStandaloneMode());

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const onAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    const standaloneQuery = window.matchMedia('(display-mode: standalone)');
    const onDisplayModeChange = () => setIsInstalled(isStandaloneMode());

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);

    if (standaloneQuery.addEventListener) {
      standaloneQuery.addEventListener('change', onDisplayModeChange);
    } else {
      standaloneQuery.addListener(onDisplayModeChange);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
      if (standaloneQuery.removeEventListener) {
        standaloneQuery.removeEventListener('change', onDisplayModeChange);
      } else {
        standaloneQuery.removeListener(onDisplayModeChange);
      }
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };

    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);

    if (choice.outcome === 'accepted') {
      setIsInstalled(true);
    }

    return choice;
  }, [deferredPrompt]);

  const installState = (() => {
    if (isInstalled) return 'installed';
    if (deferredPrompt) return 'installable';
    if (canShowIosInstallGuide()) return 'ios';
    return 'unavailable';
  })();

  const isVisible = installState === 'installable' || installState === 'ios';

  return {
    installState,
    isVisible,
    isInstalled,
    platform,
    promptInstall,
  };
}
